import BuildIcon from '@mui/icons-material/Build';
import EngineeringIcon from '@mui/icons-material/Engineering';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FaceIcon from '@mui/icons-material/Face';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import { ListSubheader } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { default as Autocomplete } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { ApiResource } from '@shared/interfaces/ApiInterfaces';
import { ModuleConfiguration } from '@shared/interfaces/CommonInterfaces';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import HeaderMenuButton from 'components/HeaderMenuButton';
import { MappedModuleWithChildren } from 'interfaces/Interfaces';
import { jwtDecode } from 'jwt-decode';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { getAccessToken } from 'utils/accessTokenStore';
import { isAdmin, stripHtml } from 'utils/utils';
import { useMaintenanceMode } from '../../contexts/MaintenanceContext';
import BackendApiService from '../../services/BackendApiService';

const IS_DEVELOPMENT = process.env.REACT_APP_ENVIRONMENT !== 'production';
const ENVIRONMENT_LABEL = process.env.REACT_APP_ENVIRONMENT === 'local'
  ? { text: 'LOCAL', backgroundColor: '#1976d2' }
  : IS_DEVELOPMENT
    ? { text: 'DEV', backgroundColor: '#ed6c02' }
    : null;

interface MainLayoutProps {
  children?: ReactNode;
  userResources?: MappedModuleWithChildren[];
}

type MenuEntry = {
  title: string;
  href?: string;
  children?: { title: string, href: string }[];
}

const UserChip = styled(Chip)(() => ({
  background: "rgba(255, 255, 255, 0.15)",
  padding: "5px",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  color: "#fff",
  background: "rgba(255, 255, 255, 0.15)",
  borderRadius: "8px",
  width: "100%",
  boxSizing: 'border-box',
  padding: "5px",
  "& input": {
    color: "#fff !important",
    padding: "5px !important"
  },

  "& fieldset": {
    borderWidth: "0px",
    border: "none",
    "& fieldset:hover, & fieldset:focus, & fieldset:active": {
      borderWidth: "0px"
    },
    "& fieldset": { border: "none" },
    "& .MuiInputBase-input": {
      transition: theme.transitions.create("width"),
      color: "#fff",
      width: "100%",
      [theme.breakpoints.up("sm")]: {
        width: "12ch",
        "&:focus": {
          width: "20ch"
        }
      }
    }
  }
}));

const getDecodedAccessToken = (token: string): any => {
  try {
    return jwtDecode(token);
  } catch (Error) {
    return null;
  }
}

const MAX_SEARCH_OPTIONS = 120;
const ICON_PREFETCH_LIMIT = 180;
const MIN_RESOURCE_SEARCH_LENGTH = 2;
const DEFAULT_RESOURCE_ICON = '/icons/default.webp';

const getResourceIconSrc = (platform?: string, name?: string) => `/icons/${platform || ''}-${name || ''}.svg`;
const loadedResourceIcons = new Set<string>();
const failedResourceIcons = new Set<string>();

const scheduleIdle = (work: () => void) => {
  const maybeWindow = window as any;
  if (typeof maybeWindow.requestIdleCallback === 'function') {
    maybeWindow.requestIdleCallback(work, { timeout: 500 });
    return;
  }
  window.setTimeout(work, 0);
};

const ResourceOptionIcon = React.memo(({ platform, name }: { platform?: string; name?: string }) => {
  const targetSrc = getResourceIconSrc(platform, name);
  const [src, setSrc] = useState<string>(() => loadedResourceIcons.has(targetSrc) ? targetSrc : DEFAULT_RESOURCE_ICON);

  useEffect(() => {
    if (!targetSrc || failedResourceIcons.has(targetSrc)) {
      setSrc(DEFAULT_RESOURCE_ICON);
      return;
    }

    if (loadedResourceIcons.has(targetSrc)) {
      setSrc(targetSrc);
      return;
    }

    let cancelled = false;
    const image = new Image();

    image.onload = () => {
      loadedResourceIcons.add(targetSrc);
      if (!cancelled) {
        setSrc(targetSrc);
      }
    };

    image.onerror = () => {
      failedResourceIcons.add(targetSrc);
      if (!cancelled) {
        setSrc(DEFAULT_RESOURCE_ICON);
      }
    };

    image.src = targetSrc;

    return () => {
      cancelled = true;
    };
  }, [targetSrc]);

  return (
    <img
      alt=""
      src={src}
      width={24}
      height={24}
      loading="eager"
      decoding="async"
    />
  );
});

function flattenUserResources(userRequests: any[]): any[] {
  const parentMap: Map<string, any> = new Map();
  const childMap: Map<string, any> = new Map();

  const isDeleted = (item: any) => item.statusType === 'delete' && item.prStatus === 'completed';
  const isSearchEligible = (item: any) => item.status !== 'cancelled' && item.status !== 'deleted';

  userRequests.forEach((parent: any) => {
    if (isDeleted(parent) || !isSearchEligible(parent)) return;
    const parentKey: string = stripHtml(parent.title);
    const parentDate: Date = new Date(parent.updatedDate || parent.creationDate || 0);
    const existingParent: any = parentMap.get(parentKey);

    if (!existingParent || parentDate > new Date(existingParent.updatedDate || existingParent.creationDate || 0)) {
      if (parent.pullRequestId > 0) {
        const parentSearchText = [
          parentKey,
          parent.displayResourceName || '',
          parent.resourceName || '',
          parent.resolvedResourceName || '',
          `${parent.parentName?.prefix || ''}${parent.resourceName || ''}${parent.parentName?.suffix || ''}`
        ].join(' ').toLowerCase();

        parentMap.set(parentKey, {
          id: parent.id,
          type: 'userResource',
          label: parentKey,
          searchText: parentSearchText,
          display: parent.title,
          name: parent.name,
          platform: parent.platform,
          pullRequestId: parent.pullRequestId,
          latestPullRequestId: parent.latestPullRequestId,
          isParent: true,
          updatedDate: parent.updatedDate,
          creationDate: parent.creationDate,
          platformTitle: "Search results ..."
        });
      }
    }

    if (Array.isArray(parent.children)) {
      parent.children.forEach((child: any) => {
        if (isDeleted(child) || !isSearchEligible(child)) return;
        const childKey: string = stripHtml(child.title);
        const childDate: Date = new Date(child.updatedDate || child.creationDate || 0);
        const existingChild: any = childMap.get(childKey);

        if (!existingChild || childDate > new Date(existingChild.updatedDate || existingChild.creationDate || 0)) {
          if (child.pullRequestId > 0) {
            const childSearchText = [
              childKey,
              child.displayResourceName || '',
              child.resourceName || '',
              child.resolvedResourceName || '',
              `${child.parentName?.prefix || ''}${child.resourceName || ''}${child.parentName?.suffix || ''}`
            ].join(' ').toLowerCase();

            childMap.set(childKey, {
              id: child.id,
              type: 'userResource',
              label: childKey,
              searchText: childSearchText,
              display: child.title,
              name: child.name,
              platform: child.platform,
              pullRequestId: child.pullRequestId,
              latestPullRequestId: child.latestPullRequestId,
              isParent: false,
              parentResource: parentKey,
              updatedDate: child.updatedDate,
              creationDate: child.creationDate,
              platformTitle: "Search results ..."
            });
          }
        }
      });
    }
  });

  return [
    ...Array.from(parentMap.values()),
    ...Array.from(childMap.values())
  ];
}



const createTree = (entries: ApiResource[]) => {
  const modulesById: { [key: string]: MappedModuleWithChildren } = {};
  const rootModules: MappedModuleWithChildren[] = [];
  const buildParentLookupKey = (name?: string, platform?: string, resource?: string) => `${name || ''}|${platform || ''}|${resource || ''}`;

  const toTimestamp = (value: Date | string | undefined): number => {
    if (!value) {
      return 0;
    }

    const ts = new Date(value).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  const shouldReplaceExisting = (existing: MappedModuleWithChildren | undefined, incoming: ApiResource): boolean => {
    if (!existing) {
      return true;
    }

    return toTimestamp(incoming.updatedDate) > toTimestamp(existing.updatedDate);
  };

  // First, map all modules by their ID for easy access
  entries.forEach(entry => {
    entry.modules.forEach(module => {
      if (shouldReplaceExisting(modulesById[module.id], entry)) {
        modulesById[module.id] = { ...entry, ...module, children: [] };
      }
    });
  });

  const modules = Object.values(modulesById);
  const parentLookupByKey = new Map<string, string>();

  modules.forEach(module => {
    parentLookupByKey.set(
      buildParentLookupKey(
        module.name,
        module.platform,
        `${module.parentName?.prefix || ''}${module.resourceName}${module.parentName?.suffix || ''}`
      ),
      module.id
    );
  });

  // Then, build the tree structure
  modules.forEach(module => {
    if (module.parent) {
      const parentId = parentLookupByKey.get(buildParentLookupKey(module.parent?.name, module.parent?.platform, module.parent?.resource));
      if (parentId && modulesById[parentId]) {
        modulesById[parentId].children?.push(module);
      } else {
        // parent not found - create "unknown" dummy parent for UI visualization
        const dummyId = `${module.parent.platform}-${module.parent.name}-${module.parent.resource}`
        let parentDummyModule = rootModules.find(m => m.id === dummyId);
        if (!parentDummyModule) {
          parentDummyModule = {
            ...module.parent,
            id: dummyId,
            resourceName: dummyId,
            title: `<span style="text-transform: capitalize">${module.parent.name}</span> (${module.parent.resource})`,
            statusDescription: "Parent not resolvable",
            children: [],
            // Additional properties
            modules: [],
            checks: {
              review: {
                status: "unknown",
                reviewers: [],
              }
            },
            pullRequestId: 0,
            latestPullRequestId: 0,
            creationDate: new Date(),
            updatedDate: new Date(),
            closedDate: new Date()
          };
          rootModules.push(parentDummyModule);
        }
        parentDummyModule.children.push(module);
      }
    } else {
      rootModules.push(module);
    }
  });

  return rootModules;
};


function MainLayout({ children, userResources }: MainLayoutProps) {
  const location = useLocation();
  const isModuleRoute = location.pathname.startsWith('/module/');

  const cleanupToolEnabled = process.env.REACT_APP_ENABLE_ADMIN_CLEANUP_TOOL === 'true';
  const importerToolEnabled = process.env.REACT_APP_ENABLE_ADMIN_MODULE_IMPORTER_TOOL === 'true';
  const reconcileToolEnabled =
    process.env.REACT_APP_ENVIRONMENT === 'local' &&
    process.env.REACT_APP_ENABLE_ADMIN_RECONCILE_MISSING_DELETE_REQUESTS_TOOL === 'true';

  const allDays = 10 * 365;


  const [userRequests, setUserRequests] = useState<MappedModuleWithChildren[]>([]);
  const [anchorElNav, setAnchorElNav] = useState<null | HTMLElement>(null);
  const [value, setValue] = useState<ModuleConfiguration | null>(null);
  const [fetchedModules, setFetchedModules] = useState<ModuleConfiguration[]>([]);
  const [flatCachedUserResources, setFlatCachedUserResources] = useState<any[]>([]);
  const [options, setOptions] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [userName, setUserName] = useState<string>("");
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [loadingModules, setLoadingModules] = useState<boolean>(true);
  const [loadingUserResources, setLoadingUserResources] = useState<boolean>(true);
  const [collapsedSearchGroups, setCollapsedSearchGroups] = useState<Record<string, boolean>>({});
  const prefetchedIconsRef = useRef<Set<string>>(new Set());
  const { maintenanceConfig, isMaintenanceMode } = useMaintenanceMode();
  const [accessToken] = useState(() => {
    return getAccessToken();
  });

  // Build pages array dynamically based on maintenance mode; Overview is reachable via the logo link.
  const pages: MenuEntry[] = [
    ...(!isMaintenanceMode ? [{
      title: 'My resources',
      href: '/user/resources'
    }] : [])
  ];

  if (['local'].includes(process.env.REACT_APP_ENVIRONMENT || '')) {
    pages.push(
      {
        title: 'Debug',
        href: '/debug'
      }
    );
  }

  if (isAdmin(accessToken ?? '')) {
    if (!pages.find(e => e.title === "Admin")) {
      pages.push({
        title: 'Admin',
        children: [
          {
            title: 'Module Configuration',
            href: '/admin/modulesconfig'
          },
          ...(importerToolEnabled ? [{
            title: 'Module Importer',
            href: '/admin/importer'
          }] : []),
          ...(cleanupToolEnabled ? [{
            title: 'Cleanup empty workspaces',
            href: '/admin/cleanup-empty-workspaces'
          }] : []),
          ...(reconcileToolEnabled ? [{
            title: 'Reconcile missing delete requests',
            href: '/admin/reconcile-missing-delete-requests'
          }] : [])
        ]
      });
    }
  }

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleCloseNavMenu = () => {
    setAnchorElNav(null);
  };

  const toggleSearchGroup = (groupName: string) => {
    setCollapsedSearchGroups((prev) => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const searchGroupOrder = useMemo(() => {
    const groupOrder: string[] = [];
    const seen = new Set<string>();

    options.forEach((option: any) => {
      const group = option?.platformTitle || '';
      if (group && !seen.has(group)) {
        seen.add(group);
        groupOrder.push(group);
      }
    });

    return groupOrder;
  }, [options]);

  useEffect(() => {
    const fetchModules = async () => {
      try {
        const backendApiService = new BackendApiService(accessToken ?? '');
        const fetchedModules = await backendApiService.getModules();
        setFetchedModules(fetchedModules);
        const initialModuleOptions = fetchedModules.map(m => ({ ...m, type: 'module' }));
        setOptions(initialModuleOptions.slice(0, MAX_SEARCH_OPTIONS));
        setLoadingModules(false);
      } catch (err) {
        console.error("Could not fetch modules", err);
        setLoadingModules(false);
      }
    };

    fetchModules();

    const tokenInfo = getDecodedAccessToken(accessToken!);
    setUserRoles(tokenInfo.roles ?? [])
    setUserName(tokenInfo.name);
  }, [accessToken]);

  const [debouncedInputValue, setDebouncedInputValue] = useState('');

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedInputValue(inputValue);
    }, 100);

    return () => {
      clearTimeout(handler);
    };
  }, [inputValue]);

  useEffect(() => {
    const modulesForSearch = fetchedModules.map(m => ({ ...m, type: 'module' }));

    if (debouncedInputValue === '') {
      const allOptions = modulesForSearch.slice(0, MAX_SEARCH_OPTIONS);
      setOptions(allOptions);
    } else {
      // Filtere Module (quick))
      const lowerCaseInput = debouncedInputValue.toLowerCase();
      const filteredModules = modulesForSearch
        .filter(e =>
          e.title.toLowerCase().includes(lowerCaseInput) ||
          e.platformTitle.toLowerCase().includes(lowerCaseInput)
        )
        .slice(0, MAX_SEARCH_OPTIONS);

      const shouldSearchUserResources = lowerCaseInput.length >= MIN_RESOURCE_SEARCH_LENGTH;

      // Filter from cached UserResources (no recalculation)
      const filteredUserResources = shouldSearchUserResources
        ? flatCachedUserResources
          .filter(r => ((r.searchText || r.label || '') as string).toLowerCase().includes(lowerCaseInput))
          .slice(0, MAX_SEARCH_OPTIONS)
        : [];

      let newOptions = [...filteredModules];

      // If UserResources are still loading, show "Loading..." entry
      if (shouldSearchUserResources && loadingUserResources) {
        if (filteredUserResources.length === 0) {
          newOptions.push({
            id: 'loading',
            type: 'loading',
            label: 'Loading data...',
            platformTitle: 'Search results ...'
          } as any);
        } else {
          newOptions.push(...filteredUserResources);
        }
      } else {
        if (filteredUserResources.length > 0) {
          newOptions.push(...filteredUserResources);
        }
      }

      setOptions(newOptions);
    }
  }, [debouncedInputValue, fetchedModules, flatCachedUserResources, loadingUserResources]);

  useEffect(() => {
    const iconSources: string[] = [];

    fetchedModules.forEach((module) => {
      iconSources.push(getResourceIconSrc(module.platform, module.name));
    });

    flatCachedUserResources.forEach((resource) => {
      iconSources.push(getResourceIconSrc(resource.platform, resource.name));
    });

    const uniqueSources = Array.from(new Set(iconSources)).slice(0, ICON_PREFETCH_LIMIT);
    const pendingSources = uniqueSources.filter((src) => !prefetchedIconsRef.current.has(src));

    if (pendingSources.length === 0) {
      return;
    }

    scheduleIdle(() => {
      pendingSources.forEach((src) => {
        const img = new Image();
        img.onload = () => {
          prefetchedIconsRef.current.add(src);
          loadedResourceIcons.add(src);
        };
        img.onerror = () => {
          failedResourceIcons.add(src);
        };
        img.src = src;
      });
    });
  }, [fetchedModules, flatCachedUserResources]);

  // Cache the flattened UserResources to avoid expensive calculations on each input change
  useEffect(() => {
    const cachedFlat = flattenUserResources(userRequests || []);
    setFlatCachedUserResources(cachedFlat);
  }, [userRequests]);

  const getUserRequests = async (accessToken: string | null, status: string, sinceDays: number | undefined, showAllResourcesFilter: boolean | undefined) => {
    setLoadingUserResources(true);
    try {
      const backendApiService = new BackendApiService(accessToken ?? '');
      const userRequests = await backendApiService.getUserRequests(status, sinceDays, showAllResourcesFilter);
      const userRequestsTree = createTree(userRequests);
      setUserRequests(userRequestsTree);
      setLoadingUserResources(false);
    } catch (err) {
      console.log(err);
      setLoadingUserResources(false);
    }
  };

  useEffect(() => {
    getUserRequests(accessToken, 'all', allDays, true);
  }, [accessToken]);

  return (
    <div>
      {maintenanceConfig.enabled && (
        <Box 
          sx={{ 
            backgroundColor: '#ffc107', 
            color: '#000', 
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            borderBottom: '1px solid #e0a800'
          }}
        >
          <BuildIcon sx={{ fontSize: 20, marginRight: '16px', color: '#000' }} />
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '4px' }}>
              {maintenanceConfig.title}
            </Typography>
            <Typography variant="body2">
              {maintenanceConfig.description}
            </Typography>
          </Box>
        </Box>
      )}
      <header>
        <AppBar position="static" style={{ background: '#333' }}>
          <Toolbar disableGutters>

            {ENVIRONMENT_LABEL && (
              <Box sx={{ flexGrow: 0, ml: '20px !important', mr: '8px', mt: '-2px !important' }}>
                <Box
                  sx={{
                    px: '10px',
                    py: '3px',
                    borderRadius: '6px',
                    backgroundColor: ENVIRONMENT_LABEL.backgroundColor,
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                    lineHeight: 2
                  }}
                >
                  {ENVIRONMENT_LABEL.text}
                </Box>
              </Box>
            )}

            <Box sx={{ flexGrow: 0 }}>
              <a href="/">
                <img src='/iac-portal-logo.png' alt="logo" />
              </a>
            </Box>

            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="main-menu"
                aria-haspopup="true"
                onClick={handleOpenNavMenu}
                color="inherit"
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="main-menu"
                anchorEl={anchorElNav}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'left',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'left',
                }}
                open={Boolean(anchorElNav)}
                onClose={handleCloseNavMenu}
                sx={{
                  display: { xs: 'block', md: 'none' },
                }}
              >
                {pages.flatMap((page) => {
                  const items = [];

                  if (page.children) {
                    items.push(
                      <ListSubheader key={`${page.title}-subheader`}>{page.title}</ListSubheader>
                    );
                  }

                  (page.children || [page]).forEach((item) => {
                    items.push(
                      <MenuItem
                        key={item.title}
                        onClick={handleCloseNavMenu}
                        component={Link}
                        to={item.href || ""}
                      >
                        <Typography textAlign="center">{item.title}</Typography>
                      </MenuItem>
                    );
                  });

                  return items;
                })}
              </Menu>
            </Box>
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
              {pages.map((page) => (
                <HeaderMenuButton
                  key={page.title}
                  title={page.title}
                  href={page.href}
                  children={page.children}
                />
              ))}
            </Box>

            <Box sx={{ flexGrow: 1, mr: '30px!important' }}>
              <Autocomplete
                noOptionsText="Not found"
                id="search-field"
                ListboxProps={{
                  sx: {
                    py: 0
                  }
                }}
                slotProps={{
                  popper: {
                    placement: 'bottom-start',
                    sx: {
                      '& .MuiAutocomplete-paper': {
                        mt: 0.75,
                        borderTopLeftRadius: 1,
                        borderTopRightRadius: 1,
                        overflow: 'hidden'
                      }
                    }
                  }
                }}
                sx={{
                  width: "100%",
                  borderWidth: "0px",
                  outline: "none"
                }}
                forcePopupIcon={false}
                getOptionLabel={option => {
                  if (!option) return '';
                  if (option.type === 'module') return option.title || '';
                  if (option.type === 'userResource') return option.label || option.display || '';
                  if (option.type === 'loading') return option.label || '';
                  return '';
                }}
                groupBy={(option) => option.platformTitle}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(x) => x}
                options={options}
                disabled={loadingModules || isMaintenanceMode}
                value={value}
                inputValue={inputValue}
                onChange={(event: any, newValue: ModuleConfiguration | null) => {
                  setValue(null);
                  setInputValue('');
                }}
                onInputChange={(event, newInputValue) => {
                  setInputValue(newInputValue);
                }}

                renderInput={(params) => (
                  <StyledTextField
                    {...params}
                    placeholder={isMaintenanceMode ? "Search disabled during maintenance" : "Create new resource, Search for resources ..."}
                    size="small"
                    InputProps={{
                      ...params.InputProps,
                      style: { height: '30px' },
                      startAdornment: (
                        <InputAdornment position="start">
                          {loadingModules ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SearchIcon style={{ color: "white", marginLeft: "8px" }} />
                          )}
                        </InputAdornment>
                      )
                    }}
                  />
                )}
                renderGroup={(params) => {
                  const isCollapsed = Boolean(collapsedSearchGroups[params.group]);
                  const isLastGroup = searchGroupOrder[searchGroupOrder.length - 1] === params.group;

                  return (
                    <li key={params.key}>
                      <Box
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => toggleSearchGroup(params.group)}
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          cursor: 'pointer',
                          px: 2,
                          py: 0.75,
                          backgroundColor: '#fff',
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.88rem',
                          borderBottom: isLastGroup ? 'none' : '1px solid rgba(0, 0, 0, 0.08)',
                          '&:hover': {
                            color: 'text.primary'
                          }
                        }}
                      >
                        <span>{params.group}</span>
                        {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                      </Box>
                      {!isCollapsed && (
                        <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none' }}>
                          {params.children}
                        </Box>
                      )}
                    </li>
                  );
                }}
                renderOption={(props, option) => {
                  if (option.type === 'loading') {
                    // Loading state while UserResources are loading
                    return (
                      <li {...props} key="loading-item">
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', padding: '8px' }}>
                          <CircularProgress size={20} sx={{ marginRight: '12px' }} />
                          <Typography>{option.label}</Typography>
                        </Box>
                      </li>
                    );
                  } else if (option.type === 'module') {
                    // This is a module, as before
                    var matches = match(option.title, inputValue);
                    const parts = parse(option.title, matches);

                    return (
                      <Link key={`${option.platform} - ${option.name}`} to={`/module/${option.platform}/${option.name}/`} style={{ color: '#333', textDecoration: 'none', width: '100%' }}>
                        <li {...props} key={`${option.platform}-${option.name}-li`}>
                          <Grid container alignItems="center">
                            <Grid item sx={{ display: 'flex', width: 44 }}>
                              <ResourceOptionIcon platform={option.platform} name={option.name} />
                            </Grid>
                            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                              {parts.map((part, index) => (
                                <Box
                                  key={index}
                                  component="span"
                                  sx={{ fontWeight: part.highlight ? 'bold' : 'regular' }}
                                >
                                  {part.text}
                                </Box>
                              ))}
                              <Typography variant="body2" color="text.secondary">
                                {option.platformTitle}
                              </Typography>
                            </Grid>
                          </Grid>
                        </li>
                      </Link>
                    );
                  } else if (option.type === 'userResource') {
                    // This is a UserResource (Parent or Child)
                    // Note: title may contain HTML, label is plain text
                    return (
                      <li {...props} key={`userResource-${option.id}`}>
                        <Link to={`/user/requests/${option.latestPullRequestId || option.pullRequestId}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', width: '100%' }}>
                          <Grid container alignItems="center">
                            <Grid item sx={{ display: 'flex', width: 44 }}>
                              <ResourceOptionIcon platform={option.platform} name={option.name} />
                            </Grid>
                            <Grid item sx={{ width: 'calc(100% - 44px)', wordWrap: 'break-word' }}>
                              <Box
                                component="span"
                                sx={{ fontWeight: option.isParent ? 'bold' : 'normal', color: '#1976d2' }}
                              >
                                {stripHtml(option.display)}
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                Platform: {option.platform}
                                {option.parentResource ? ` – ${option.parentResource}` : ''}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Link>
                      </li>
                    );
                  } else {
                    // Fallback for unknown types
                    return null;
                  }
                }}


              />
            </Box>

            <Box sx={{ flexGrow: 0, mr: '15px!important' }}>
              <UserChip icon={userRoles.includes("IaC.Admin") ? <EngineeringIcon /> : <FaceIcon />} label={userName} color='primary' sx={{ color: 'white' }} />
            </Box>

            <Box sx={{ flexGrow: 0, mr: '15px!important' }}>
              <img src='/wacker-logo.svg' alt="logo" height={28} />
            </Box>

            <Box sx={{ flexGrow: 0 }}>
              <a href="/logout">
                <LogoutIcon sx={{ color: 'white', mr: '10px' }} />
              </a>
            </Box>
          </Toolbar>
        </AppBar >
      </header>
      {(Array.isArray(children) && children.length === 2) &&
        <div className='info-content'>
          {children[0]}
        </div>
      }
      <main className={isModuleRoute ? 'main-content main-content-plain' : 'main-content'}>
        {/*(Array.isArray(children) && children.length === 2) ? children[1] : children*/}
        <Outlet />
      </main>
      <footer>Version: {process.env.REACT_APP_VERSION_NUMBER} {IS_DEVELOPMENT && `(${process.env.REACT_APP_ENVIRONMENT})`}</footer>
      <br></br>
    </div >
  );
}

export default MainLayout;